import React from 'react';
import { useNavigate } from 'react-router';
import { Body1, Body5, Body7, Container, Dialog } from '@ui';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { FilterGroup, GroupLabel, SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { FmdcTenantContext, useVocabularies, vocabularyOptions } from './FmdcTenantContext';
import { useUrlFilters } from '@hooks/application/useUrlFilters';
import { useDebouncedSearch } from './useDebouncedSearch';
import { useCatalogItems } from './useCatalogItems';
import { useProposals } from './useProposals';
import { CatalogItemsTable } from './CatalogItemsTable';
import { CatalogItemDrawer } from './CatalogItemDrawer';
import { CatalogItemForm, FORM_MODES } from './CatalogItemForm';
import { ProposalsTable } from './ProposalsTable';
import { Pager } from './Pager';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { GateNotice } from './GateNotice';
import { CATALOG_STATUS_FILTERS, CATALOG_TABS, PROPOSAL_STATUS_FILTERS, catalogUrl, fmdcPaths } from './fmdcUrl';
import { CATALOG_STATUS_TOKENS, PROPOSAL_STATUS_TOKENS } from './statusTokens';
import { FilterRow, FilterSelect, SearchInput } from './styles';
import { formatCount } from './format';

// The catalog and its proposals. Everyone reads; admins add and edit items and decide proposals.
// Filters live in the URL (catalogUrl), the tab included, so a link to the proposals tab is a
// link to the proposals tab.

const TAB_SEGMENTS = [
  { key: CATALOG_TABS.ITEMS, label: 'Items' },
  { key: CATALOG_TABS.PROPOSALS, label: 'Proposals' },
];

const ITEM_STATUS_SEGMENTS = [
  { key: CATALOG_STATUS_FILTERS.ACTIVE, label: 'Active', token: CATALOG_STATUS_TOKENS.active },
  { key: CATALOG_STATUS_FILTERS.PROPOSED, label: 'Proposed', token: CATALOG_STATUS_TOKENS.proposed },
  { key: CATALOG_STATUS_FILTERS.RETIRED, label: 'Retired', token: CATALOG_STATUS_TOKENS.retired },
  { key: CATALOG_STATUS_FILTERS.ALL, label: 'All', token: 'text' },
];

const PROPOSAL_STATUS_SEGMENTS = [
  { key: PROPOSAL_STATUS_FILTERS.PENDING, label: 'Pending', token: PROPOSAL_STATUS_TOKENS.pending },
  { key: PROPOSAL_STATUS_FILTERS.APPROVED, label: 'Approved', token: PROPOSAL_STATUS_TOKENS.approved },
  { key: PROPOSAL_STATUS_FILTERS.REJECTED, label: 'Rejected', token: PROPOSAL_STATUS_TOKENS.rejected },
  { key: PROPOSAL_STATUS_FILTERS.ALL, label: 'All', token: 'text' },
];

const isFilteringItems = (filters) => !!filters.q || !!filters.section || filters.status !== catalogUrl.defaults.status;

// The search box is bound to local state and written to the URL after a pause, so a reload mid-word
// does not fire a request per keystroke.

export const CatalogView = () => {
  const navigate = useNavigate();
  const { tenant, canAdmin } = React.useContext(FmdcTenantContext);
  const { vocabularies } = useVocabularies();
  const { filters, setFilters } = useUrlFilters(catalogUrl);

  const isItems = filters.tab === CATALOG_TABS.ITEMS;
  const items = useCatalogItems(filters, { enabled: isItems });
  const proposals = useProposals(filters, { enabled: !isItems });

  const [query, setQuery] = useDebouncedSearch(filters.q, (q) => setFilters({ q }));
  const [openItem, setOpenItem] = React.useState(null);
  const [isAdding, setIsAdding] = React.useState(false);

  const sectionOptions = vocabularyOptions(vocabularies, 'section');
  const embeddingsStale = tenant?.catalog?.embeddings_stale === true;

  const openRun = (runId) => navigate(fmdcPaths.run(ROUTES.fmdc, runId));

  const handleItemSaved = (saved) => {
    items.reload();
    // Keep the drawer on the saved record so the admin sees what landed.
    if (openItem && saved) setOpenItem(saved);
  };

  const handleCreated = () => {
    setIsAdding(false);
    items.reload();
  };

  const clearItemFilters = () => {
    setQuery('');
    setFilters({ q: '', section: '', status: catalogUrl.defaults.status });
  };

  const renderItems = () => {
    if (items.isLoading) return <Body5 isLowContrast>Loading catalog…</Body5>;
    if (items.error && (!items.items || items.items.length === 0)) {
      return (
        <div>
          <Body5>{items.error}</Body5>
          <Container top={12}>
            <SkeletonButton onClick={items.reload}>Retry</SkeletonButton>
          </Container>
        </div>
      );
    }
    if (items.items.length === 0) {
      return isFilteringItems(filters) ? (
        <GateNotice compact title="No catalog items match" body="Try another search, section or status.">
          <TransparentFlatButton onClick={clearItemFilters}>Clear filters</TransparentFlatButton>
        </GateNotice>
      ) : (
        <GateNotice compact title="The catalog is empty" body="Items arrive with the client's template setup." />
      );
    }
    return (
      <>
        {items.error && (
          <Container bottom={12}>
            <NoticeText tone={NOTICE_TONES.ERROR}>{items.error}</NoticeText>
          </Container>
        )}
        <CatalogItemsTable items={items.items} onOpen={setOpenItem} />
        <Pager
          offset={filters.offset}
          total={items.total}
          count={items.items.length}
          onChange={(offset) => setFilters({ offset })}
        />
      </>
    );
  };

  const renderProposals = () => {
    if (proposals.isLoading) return <Body5 isLowContrast>Loading proposals…</Body5>;
    if (proposals.error && (!proposals.proposals || proposals.proposals.length === 0)) {
      return (
        <div>
          <Body5>{proposals.error}</Body5>
          <Container top={12}>
            <SkeletonButton onClick={proposals.reload}>Retry</SkeletonButton>
          </Container>
        </div>
      );
    }
    if (proposals.proposals.length === 0) {
      return filters.proposalStatus === PROPOSAL_STATUS_FILTERS.PENDING ? (
        <GateNotice
          compact
          title="No pending proposals"
          body="A run proposes an item when a model carries a custom metric the catalog does not hold."
        >
          <SkeletonButton onClick={() => setFilters({ proposalStatus: PROPOSAL_STATUS_FILTERS.ALL })}>
            Show all proposals
          </SkeletonButton>
        </GateNotice>
      ) : (
        <GateNotice compact title="No proposals match" />
      );
    }
    return (
      <>
        {proposals.error && (
          <Container bottom={12}>
            <NoticeText tone={NOTICE_TONES.ERROR}>{proposals.error}</NoticeText>
          </Container>
        )}
        <ProposalsTable
          proposals={proposals.proposals}
          canAdmin={canAdmin}
          onOpenRun={openRun}
          onDecided={() => {
            proposals.reload();
          }}
        />
        <Pager
          offset={filters.offset}
          total={proposals.total}
          count={proposals.proposals.length}
          onChange={(offset) => setFilters({ offset })}
        />
      </>
    );
  };

  return (
    <div>
      <Container row spaceBetween verticallyCenter flexWrap gap={12}>
        <SegmentedControl
          label="Show"
          options={TAB_SEGMENTS}
          active={filters.tab}
          onSelect={(tab) => setFilters({ tab })}
          segmentWidth={110}
        />
        {isItems && canAdmin && <FlatButton onClick={() => setIsAdding(true)}>Add item</FlatButton>}
      </Container>

      {embeddingsStale && (
        <Container top={16}>
          <NoticeText tone={NOTICE_TONES.NEUTRAL}>
            Catalog refreshing… aliases and embeddings are being rebuilt after an edit.
          </NoticeText>
        </Container>
      )}

      <Container top={24}>
        {isItems ? (
          <FilterRow>
            <FilterGroup>
              <GroupLabel as="label" htmlFor="fmdc-catalog-search">
                Search
              </GroupLabel>
              <SearchInput
                id="fmdc-catalog-search"
                aria-label="Search catalog items"
                placeholder="Code, label or alias…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <GroupLabel as="label" htmlFor="fmdc-catalog-section">
                Section
              </GroupLabel>
              <FilterSelect
                id="fmdc-catalog-section"
                aria-label="Filter by section"
                value={filters.section}
                onChange={(e) => setFilters({ section: e.target.value })}
              >
                <option value="">All sections</option>
                {sectionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>
            <SegmentedControl
              label="Status"
              options={ITEM_STATUS_SEGMENTS}
              active={filters.status}
              onSelect={(status) => setFilters({ status })}
              withDots
            />
          </FilterRow>
        ) : (
          <FilterRow>
            <SegmentedControl
              label="Status"
              options={PROPOSAL_STATUS_SEGMENTS}
              active={filters.proposalStatus}
              onSelect={(proposalStatus) => setFilters({ proposalStatus })}
              withDots
            />
          </FilterRow>
        )}

        {/* The count line under the filter bar. Hidden while the body shows an empty state, which
            carries its own count and its own Clear filters. */}
        {isItems && !items.isLoading && items.items && items.items.length > 0 && (
          <Container top={12} row verticallyCenter gap={12}>
            <Body7 isLowContrast role="status">
              {formatCount(items.total)} item{items.total === 1 ? '' : 's'}
            </Body7>
            {isFilteringItems(filters) && (
              <TransparentFlatButton onClick={clearItemFilters}>Clear filters</TransparentFlatButton>
            )}
          </Container>
        )}
        {!isItems && !proposals.isLoading && proposals.proposals && proposals.proposals.length > 0 && (
          <Container top={12} row verticallyCenter gap={12}>
            <Body7 isLowContrast role="status">
              {formatCount(proposals.total)} proposal{proposals.total === 1 ? '' : 's'}
            </Body7>
          </Container>
        )}
      </Container>

      <Container top={16}>{isItems ? renderItems() : renderProposals()}</Container>

      {openItem && (
        <CatalogItemDrawer
          item={openItem}
          canAdmin={canAdmin}
          onClose={() => setOpenItem(null)}
          onSaved={handleItemSaved}
        />
      )}

      {isAdding && canAdmin && (
        <Dialog label="Add catalog item" onDismiss={() => setIsAdding(false)} width="min(640px, 90vw)">
          <Body1 bold>Add catalog item</Body1>
          <Container top={16}>
            <CatalogItemForm mode={FORM_MODES.CREATE} onSaved={handleCreated} onCancel={() => setIsAdding(false)} />
          </Container>
        </Dialog>
      )}
    </div>
  );
};
